/**
 * Social sharing helpers for Corporaco articles.
 *
 * Crawler-friendly preview URL pattern:
 *   /api/share/{slug} returns server-rendered HTML with proper OG/Twitter tags
 *   so LinkedIn / X / Facebook / WhatsApp render the article cover + excerpt.
 *   Humans get meta-refreshed straight into the React route.
 *
 * Instagram has NO public share API. We:
 *   1. Copy "title + summary + hashtags + link" to clipboard
 *   2. Download the cover image so the user can attach it manually
 *   3. Open instagram.com in a new tab
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Build the crawler-friendly share URL pointing at the backend OG endpoint.
 * Uses window.location.origin (the canonical domain the visitor is on, e.g.
 * https://corporaco.com) instead of REACT_APP_BACKEND_URL so the link in
 * social previews matches the site the user is actually using.
 */
export function shareUrlFor(slug) {
  if (!slug) return "";
  const origin = (typeof window !== "undefined" && window.location?.origin) || BACKEND_URL;
  return `${origin}/api/share/${slug}`;
}

function buildCaption({ title, summary, url, hashtags = [] }) {
  const tagLine = hashtags.length ? "\n\n" + hashtags.map((h) => `#${h}`).join(" ") : "";
  return `${title}\n\n${summary || ""}\n\n${url}${tagLine}`.trim();
}

export async function copyCaptionToClipboard({ title, summary, url, hashtags = [] }) {
  const text = buildCaption({ title, summary, url, hashtags });
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function shareLinkedIn({ slug, url }) {
  const target = shareUrlFor(slug) || url;
  const u = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(target)}`;
  window.open(u, "_blank", "noopener,noreferrer,width=720,height=640");
}

/**
 * Download the cover image to the user's device, so they can attach it
 * when posting on Instagram (or anywhere). Tries fetch+blob first so
 * cross-origin images still work; falls back to a normal anchor download.
 */
export async function downloadCoverImage({ coverUrl, filename = "corporaco-cover.jpg" }) {
  if (!coverUrl) return false;
  try {
    const r = await fetch(coverUrl, { mode: "cors" });
    if (!r.ok) throw new Error("fetch failed");
    const blob = await r.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    return true;
  } catch {
    // Fallback — opens the image in a new tab; user can right-click → save
    window.open(coverUrl, "_blank", "noopener,noreferrer");
    return false;
  }
}

/**
 * Instagram flow: copies caption to clipboard, downloads cover image, then
 * opens instagram.com so the user can paste the caption and attach the image.
 */
export async function shareInstagram({ title, summary, url, hashtags = [], coverUrl, slug }) {
  const finalUrl = shareUrlFor(slug) || url;
  const copied = await copyCaptionToClipboard({ title, summary, url: finalUrl, hashtags });
  await downloadCoverImage({
    coverUrl,
    filename: `${(slug || "corporaco-cover").replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}.jpg`,
  });
  window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  return copied;
}

export function shareTwitter({ title, slug, url, hashtags = [] }) {
  const target = shareUrlFor(slug) || url;
  const txt = `${title}\n\n${target}`;
  const tags = hashtags.slice(0, 3).join(",");
  const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(txt)}${tags ? `&hashtags=${encodeURIComponent(tags)}` : ""}`;
  window.open(u, "_blank", "noopener,noreferrer,width=640,height=520");
}

export async function copyLink(url) {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}
